import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getApiErrorMessage, portalApi } from '../../api/client';
import type { DashboardResponse } from '../../api/types';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { formatAmount } from '../../utils/format';

const RANGES = [
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
] as const;

export function DashboardPage() {
  const { balances, merchant } = useAuth();
  const walletMissing = merchant?.settlement_wallet_configured !== true;
  const { environment, isSandbox } = useEnvironment();
  const [range, setRange] = useState<string>('7d');
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void portalApi.dashboard(environment, range).then(setData).catch((e) => setError(getApiErrorMessage(e)));
  }, [environment, range]);

  const chartData =
    data?.volume_series.map((r) => ({
      date: new Date(r.bucket).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      volume: Number(r.volume),
      tx: Number(r.tx_count),
    })) ?? [];

  const stroke = isSandbox ? '#a78bfa' : '#d4af37';
  const b = data?.balances ?? balances;

  return (
    <div className="space-y-6">
      {walletMissing && merchant?.status === 'active' ? (
        <div className="rounded-xl border border-onyx-gold/40 bg-onyx-gold/10 px-4 py-3 text-sm">
          <strong className="text-onyx-gold">Action required:</strong> configure your{' '}
          <Link to="/settlement" className="underline font-semibold">
            settlement wallet
          </Link>{' '}
          to accept on-chain payments and pair POS terminals.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-display text-2xl">Overview</h2>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRange(r.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                range === r.id
                  ? isSandbox
                    ? 'bg-onyx-purple/20 text-onyx-purple'
                    : 'bg-onyx-gold/20 text-onyx-gold'
                  : 'text-onyx-muted hover:bg-onyx-elevated'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {error ? <p className="text-onyx-danger text-sm">{error}</p> : null}
      <div className="grid md:grid-cols-3 gap-4">
        <Card title="Available balance">
          <p className="text-2xl font-semibold">{b ? formatAmount(b.available_balance, b.currency) : '—'}</p>
          <p className="text-xs text-onyx-muted mt-1">Withdrawable now</p>
        </Card>
        <Card title="Pending balance">
          <p className="text-2xl font-semibold">{b ? formatAmount(b.pending_balance, b.currency) : '—'}</p>
          <p className="text-xs text-onyx-muted mt-1">In gateway processing</p>
        </Card>
        <Card title="Rolling reserve">
          <p className="text-2xl font-semibold">{b ? formatAmount(b.rolling_reserve, b.currency) : '—'}</p>
          <p className="text-xs text-onyx-muted mt-1">{b?.reserve_percent ?? 5}% of confirmed volume</p>
        </Card>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Card title="Sales volume" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2a2a32" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8b8b96" fontSize={11} />
                <YAxis stroke="#8b8b96" fontSize={11} />
                <Tooltip contentStyle={{ background: '#1a1a20', border: '1px solid #2a2a32' }} />
                <Area type="monotone" dataKey="volume" stroke={stroke} fill="url(#volGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="By payment method">
          <ul className="space-y-3">
            {(data?.methods ?? []).map((m) => (
              <li key={m.method} className="flex justify-between text-sm">
                <span className="capitalize text-onyx-muted">{m.method}</span>
                <span className="font-medium">{formatAmount(m.total)}</span>
              </li>
            ))}
            {!data?.methods?.length ? <li className="text-onyx-muted text-sm">No data in range</li> : null}
          </ul>
        </Card>
      </div>
    </div>
  );
}
